/**
 * DemoOrderIntake - Shareable order form demo
 * 
 * Shows the power of external order links:
 * - Left: Link generation success state (tiny, subtle)
 * - Right: Mobile phone mockup with clean 4-5 field form
 * 
 * Philosophy: "Links are powerful, forms are boring"
 * Shows frictionlessness, not completeness.
 * 
 * GUARDRAIL: This component uses only static demo data.
 * No imports from hooks/, features/, or services/.
 */

import { Check, Copy, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

export function DemoOrderIntake() {
  const { t } = useLanguage();
  
  return (
    <div className="grid md:grid-cols-2 gap-8 items-center">
      {/* Left Side: Link Generation Success State */}
      <div className="flex flex-col items-center md:items-end gap-4">
        {/* Success indicator */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-4 max-w-xs w-full">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-800/50 flex items-center justify-center">
              <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
              {t("landing.demo.linkCreated")}
            </span>
          </div>
          
          {/* Link preview */}
          <div className="bg-background rounded border border-border/50 px-3 py-2 flex items-center gap-2">
            <span className="text-xs text-muted-foreground truncate flex-1 font-mono">
              orderflow.ge/order/bakery-2024
            </span>
            <button 
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t("landing.demo.copyLink") || "Copy link"}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          
          {/* Action hint */}
          <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
            <ExternalLink className="h-3 w-3" />
            {t("landing.demo.shareOn")}
          </p>
        </div>
      </div>
      
      {/* Right Side: Phone Mockup with Form */}
      <div className="flex justify-center md:justify-start">
        <div className="relative">
          {/* Phone Frame */}
          <div className="w-[260px] h-[480px] bg-card rounded-[2.5rem] border-4 border-foreground/10 shadow-xl overflow-hidden">
            {/* Notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-foreground/10 rounded-full" />
            
            {/* Screen Content */}
            <div className="h-full pt-8 pb-4 px-4 overflow-hidden">
              {/* Header */}
              <div className="text-center mb-4 pt-2">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <span className="text-lg">🍞</span>
                </div>
                <h3 className="text-sm font-bold text-foreground">{t("landing.demo.familyBakery")}</h3>
                <p className="text-[10px] text-muted-foreground">{t("landing.demo.orderForm")}</p>
              </div>
              
              {/* Form Fields - Clean, minimal */}
              <div className="space-y-3">
                {/* Name */}
                <div>
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">{t("landing.demo.formLabels.name")}</label>
                  <div className="h-8 bg-muted/50 rounded border border-border/50 px-2 flex items-center">
                    <span className="text-xs text-foreground">{t("landing.demo.sampleNames.george").split(" ")[0]}</span>
                  </div>
                </div>
                
                {/* Phone */}
                <div>
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">{t("landing.demo.formLabels.phone")}</label>
                  <div className="h-8 bg-muted/50 rounded border border-border/50 px-2 flex items-center">
                    <span className="text-xs text-foreground">555 12 34 56</span>
                  </div>
                </div>
                
                {/* Address */}
                <div>
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">{t("landing.demo.formLabels.address")}</label>
                  <div className="h-8 bg-muted/50 rounded border border-border/50 px-2 flex items-center">
                    <span className="text-xs text-foreground truncate">{t("landing.demo.sampleAddresses.vake")}</span>
                  </div>
                </div>
                
                {/* Quantity */}
                <div>
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">{t("landing.demo.formLabels.quantity")}</label>
                  <div className="h-8 bg-muted/50 rounded border border-border/50 px-2 flex items-center">
                    <span className="text-xs text-foreground">2 {t("landing.demo.formLabels.pieces")}</span>
                  </div>
                </div>
                
                {/* Comment - optional feel */}
                <div>
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">{t("landing.demo.formLabels.note")}</label>
                  <div className="h-8 bg-muted/30 rounded border border-border/30 px-2 flex items-center">
                    <span className="text-xs text-muted-foreground italic">{t("landing.demo.formLabels.optional")}</span>
                  </div>
                </div>
              </div>
              
              {/* Submit Button - disabled/muted appearance */}
              <div className="mt-4">
                <div className={cn(
                  "w-full h-10 rounded-lg flex items-center justify-center",
                  "bg-primary text-primary-foreground text-sm font-medium"
                )}>
                  {t("landing.demo.submit")}
                </div>
              </div>
            </div>
          </div>
          
          {/* Decorative glow */}
          <div className="absolute -inset-4 bg-gradient-to-br from-primary/5 to-transparent rounded-[3rem] -z-10" />
        </div>
      </div>
    </div>
  );
}